import { apiClient } from './client';
import { anonymousTestHandler } from '../anonymousTestHandler';

export interface Question {
  _id: string;
  Question: string;  // Capitalized to match backend
  Type: number;      // Capitalized to match backend
  Category: string;  // Capitalized to match backend
}

export interface TestSession {
  test_id: string;
  temp_user_id: string | null;
  questions: Question[];
}

export interface SaveAnswerRequest {
  question_id: string;
  value: number;
  temp_user_id: string | null;
}

export interface TestResults {
  primaryType: number | string; // Support both Enneagram (number) and Four Temperaments (string)
  wing?: number | null;
  scores: {
    [key: string]: number
  };
  resultsToken?: string;
}

export const questionsApi = {
  getTestQuestions: async (): Promise<TestSession> => {
    try {
      console.log('🔗 Fetching test questions with baseURL:', apiClient.defaults.baseURL);
      const response = await apiClient.get('/questions/test');
      console.log('✅ Got test questions response:', response);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting test questions:', error);
      throw error;
    }
  },

  saveAnswer: async (testId: string, data: SaveAnswerRequest): Promise<void> => {
    try {
      console.log('💾 Saving answer:', { testId, data });
      await apiClient.post(`/questions/test/${testId}/answer`, data);
      console.log('✅ Answer saved successfully');
    } catch (error) {
      console.error('❌ Error saving answer:', error);
      throw error;
    }
  },

  submitTest: async (testId: string, data: {
    temp_user_id: string | null;
    answers?: { [key: string]: number }
  }): Promise<TestResults> => {
    try {
      console.log('📤 Submitting test:', { testId, data });
      const response = await apiClient.post(`/questions/test/${testId}/results`, data);
      console.log('✅ Test submission response:', response.data);

      // Ensure the response has the expected format
      const results = response.data;
      // Accept both number (Enneagram) and string (Four Temperaments) primaryType
      const hasValidPrimaryType = results.primaryType &&
        (typeof results.primaryType === 'number' || typeof results.primaryType === 'string');

      if (!results || !hasValidPrimaryType || !results.scores) {
        console.error('❌ Invalid results format:', {
          hasResults: !!results,
          primaryType: results?.primaryType,
          primaryTypeType: typeof results?.primaryType,
          hasScores: !!results?.scores
        });
        throw new Error('Invalid results format from server');
      }

      // Store the results token using the anonymousTestHandler service
      if (results.resultsToken) {
        console.log('💾 Storing anonymous test results in the handler service');
        anonymousTestHandler.storeTestResults(results.resultsToken, results.primaryType);
      }

      return results;
    } catch (error) {
      console.error('❌ Error submitting test with POST, trying GET workaround:', error);

      // WORKAROUND: Try GET request if POST fails
      try {
        console.log('🔄 Attempting GET workaround...');
        const answersJson = JSON.stringify(data.answers || {});
        const encodedAnswers = encodeURIComponent(answersJson);
        const getResponse = await apiClient.get(`/questions/test/${testId}/calculate?temp_user_id=${data.temp_user_id}&answers=${encodedAnswers}`);

        console.log('✅ GET workaround successful:', getResponse.data);

        const results = getResponse.data;
        const hasValidPrimaryType = results.primaryType &&
          (typeof results.primaryType === 'number' || typeof results.primaryType === 'string');

        if (!results || !hasValidPrimaryType || !results.scores) {
          throw new Error('Invalid results format from GET workaround');
        }

        if (results.resultsToken) {
          anonymousTestHandler.storeTestResults(results.resultsToken, results.primaryType);
        }

        return results;
      } catch (workaroundError) {
        console.error('❌ GET workaround also failed:', workaroundError);
        throw error; // Throw original POST error
      }
    }
  }
};